import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { PhoneVerify } from "@/components/PhoneVerify";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", phone_token: "", password: "", referral_code: (params.get("ref") || "").toUpperCase() });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.phone_token) {
      setError("Please verify your mobile number with the OTP first");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 py-16" data-testid="register-page">
      <div className="grain-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" data-testid="register-logo-link" className="flex flex-col items-center gap-4 mb-12">
          <img src="/logo-mark.png" alt="MetrixWorks logo" className="h-16 w-16 bg-white p-1" data-testid="register-logo-image" />
          <span className="flex items-baseline gap-2">
            <span className="font-display text-2xl">MetrixWorks</span>
            <span className="font-accent text-[10px] tracking-[0.25em] text-[#D4AF37] uppercase">Service Pvt Ltd</span>
          </span>
        </Link>
        <div className="bg-[#0A0A0A] border border-white/10 p-10">
          <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/35 mb-2">Become an Investor</p>
          <h1 className="font-display text-3xl tracking-tight mb-10">Open your account.</h1>
          {error && (
            <p data-testid="register-error" className="mb-6 text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>
          )}
          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <input data-testid="register-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="register-email-input" type="email" required placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} />
            <PhoneVerify
              phone={form.phone}
              email={form.email}
              onPhone={(phone) => setForm({ ...form, phone, phone_token: "" })}
              verified={!!form.phone_token}
              onVerified={(phone_token, phone) => setForm({ ...form, phone_token, phone })}
            />
            <input data-testid="register-password-input" type="password" required minLength={6} placeholder="Password (min. 6 characters)" value={form.password} onChange={set("password")} className={inputCls} />
            <input data-testid="register-referral-input" placeholder="Referral code (optional)" value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} className={`${inputCls} uppercase tracking-widest`} />
            <button
              data-testid="register-submit-button"
              disabled={loading || !form.phone_token}
              className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50"
            >
              {loading ? "Creating…" : form.phone_token ? "Create Account" : "Verify code to continue"}
            </button>
          </form>
          <p className="mt-8 text-sm text-white/40">
            Already an investor?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-[#D4AF37] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
